import React, { useEffect, useRef, useCallback, useId } from 'react';

/**
 * #285 B5 — one accessible modal primitive for the whole dashboard.
 *
 * Provides what every ad-hoc overlay was missing: `role="dialog"` +
 * `aria-modal`, a labelled title (`aria-labelledby`), a focus trap, Escape to
 * close, backdrop-click to close, and focus restoration to the element that
 * opened it. Callers keep rendering their own body; they just wrap it.
 *
 *   <Modal open={x} onClose={() => setX(false)} title="Topic suggestions">
 *     …body…
 *   </Modal>
 *
 * Renders nothing when `open` is false (so callers can mount it unconditionally
 * or gate on their own state — both work).
 */

interface ModalProps {
  open: boolean;
  onClose: () => void;
  /** Visible heading text — becomes the dialog's accessible name. */
  title: string;
  /** Hide the default header (caller renders its own titled header). Still labelled via `title` for AT. */
  hideHeader?: boolean;
  /** Max width of the panel (px). Default 560. */
  maxWidth?: number;
  children: React.ReactNode;
}

const FOCUSABLE = 'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

export default function Modal({ open, onClose, title, hideHeader, maxWidth = 560, children }: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId(); // SSR-safe, stable, unique
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  // Escape to close + focus trap (Tab wraps within the panel).
  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { e.stopPropagation(); onClose(); return; }
    if (e.key !== 'Tab') return;
    const panel = panelRef.current;
    if (!panel) return;
    const nodes = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((n) => n.offsetParent !== null);
    if (nodes.length === 0) { e.preventDefault(); return; }
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    const active = document.activeElement as HTMLElement | null;
    if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
  }, [onClose]);

  // On open: remember the trigger, move focus into the panel. On close: restore.
  useEffect(() => {
    if (!open) return;
    restoreFocusRef.current = (document.activeElement as HTMLElement) ?? null;
    const panel = panelRef.current;
    const target = panel?.querySelector<HTMLElement>(FOCUSABLE) ?? panel;
    target?.focus();
    return () => { restoreFocusRef.current?.focus?.(); };
  }, [open]);

  if (!open) return null;

  return (
    <div
      role="presentation"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 400,
        background: 'rgba(10,10,10,0.45)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '10vh 20px 20px', overflowY: 'auto',
      }}
    >
      <div
        ref={panelRef}
        onKeyDown={onKeyDown}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        style={{
          width: '100%', maxWidth,
          background: 'var(--bg-raised)',
          border: '1px solid var(--border)',
          boxShadow: '0 20px 60px rgba(10,10,10,0.25)',
          outline: 'none',
        }}
        tabIndex={-1}
      >
        {hideHeader ? (
          <h2 id={titleId} style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clipPath: 'inset(50%)', whiteSpace: 'nowrap', border: 0 }}>{title}</h2>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '16px 18px', borderBottom: '1px solid var(--border)' }}>
            <h2 id={titleId} style={{ margin: 0, fontSize: 'var(--fs-h3)', fontWeight: 600, color: 'var(--text-primary)' }}>{title}</h2>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close dialog"
              style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', fontSize: 20, lineHeight: 1, cursor: 'pointer', padding: '0 4px' }}
            >
              &times;
            </button>
          </div>
        )}
        {children}
      </div>
    </div>
  );
}
